import { Container, Graphics, Sprite, Text, Texture, Ticker } from 'pixi.js';
import { PaylineOverlay, lineColor } from './PaylineOverlay.ts';
import { BigWinBanner } from './BigWinBanner.ts';
import { Reel } from './Reel.ts';
import { rollup, type RollupHandle } from '../utils/rollup.ts';
import { sfx } from '../audio/sfx.ts';
import type { SpinResponse, LineWin } from '../provider/ProviderClient.ts';

const ALL_LINES_MS = 600;
const SINGLE_LINE_HOLD_MS = 900;
const BIG_WIN_MULT = 15;

interface PresenterOpts {
  cell: number;
  reelsCount: number;
  rows: number;
  reels: Reel[];
  ticker: Ticker;
  textures: Record<string, Texture>;
  fmt: (n: number) => string;
}

/**
 * Sequences the win show after reels settle: all-lines flash → per-line cycle
 * with callouts → floating rollup; big wins get the banner; expanding-special
 * spins get the expand animation first. Everything is board-space in `view`.
 */
export class WinPresenter {
  private readonly overlay: PaylineOverlay;
  private readonly banner: BigWinBanner;
  private readonly fx = new Container();
  private readonly callout: Text;
  private readonly floating: Text;
  private readonly opts: PresenterOpts;
  private paylines: number[][] = [];
  private gen = 0;
  private floatingRollup: RollupHandle | null = null;

  constructor(opts: PresenterOpts) {
    this.opts = opts;
    const boardW = opts.reelsCount * opts.cell;
    const boardH = opts.rows * opts.cell;
    this.overlay = new PaylineOverlay(opts.cell, opts.reelsCount, [], opts.ticker);
    this.banner = new BigWinBanner(boardW, boardH, opts.ticker, opts.fmt);

    this.callout = new Text({
      text: '',
      style: {
        fontFamily: 'Georgia, serif',
        fontSize: 26,
        fontWeight: 'bold',
        fill: 0xffd45e,
        stroke: { color: 0x1a0f04, width: 5 },
        letterSpacing: 1,
      },
    });
    this.callout.anchor.set(0.5);
    this.callout.position.set(boardW / 2, boardH + 34);
    this.callout.visible = false;

    this.floating = new Text({
      text: '',
      style: {
        fontFamily: 'Georgia, serif',
        fontSize: 52,
        fontWeight: 'bold',
        fill: 0xffd45e,
        stroke: { color: 0x3a2405, width: 6 },
      },
    });
    this.floating.anchor.set(0.5);
    this.floating.visible = false;
  }

  /** Add the presentation layers to the board root in draw order. */
  mount(root: Container): void {
    root.addChild(this.overlay.view, this.fx, this.callout, this.floating, this.banner.view);
  }

  setPaylines(paylines: number[][]): void {
    this.paylines = paylines || [];
    this.overlay.setPaylines(this.opts.cell, this.opts.reelsCount, this.paylines);
  }

  /** Abort any running presentation and reset visuals (called before each spin). */
  cancel(): void {
    this.gen++;
    this.overlay.clear();
    this.banner.cancel();
    this.floatingRollup?.cancel();
    this.floatingRollup = null;
    this.callout.visible = false;
    this.floating.visible = false;
    this.fx.removeChildren().forEach((c) => c.destroy());
    for (const reel of this.opts.reels) reel.clearWinning();
  }

  /** The main win show. Resolves when the presentation is over. */
  async present(result: SpinResponse, bet: number): Promise<void> {
    const gen = ++this.gen;
    const { rows, reels } = this.opts;

    const lineWins: LineWin[] = result.lineWins
      ?? (result.winningLines || []).map((li) => ({ lineIndex: li, symbol: '', count: this.opts.reelsCount, amount: 0 }));

    // Winning cell set: paying (left-aligned count) cells per line, all book
    // cells on scatter, every cell of an expanded reel.
    const winCells = new Set<string>();
    for (const lw of lineWins) {
      const line = this.paylines[lw.lineIndex];
      if (!line) continue;
      for (let col = 0; col < lw.count; col++) winCells.add(`${col},${line[col]}`);
    }
    if ((result.scatterWin ?? 0) > 0) {
      result.reels.forEach((column, col) =>
        column.forEach((s, row) => s === 'book' && winCells.add(`${col},${row}`)));
    }
    if (result.expanded) {
      for (const col of result.expanded.reels) {
        for (let r = 0; r < rows; r++) winCells.add(`${col},${r}`);
      }
    }
    if (winCells.size === 0 && lineWins.length === 0) return;

    const isBigWin = bet > 0 && result.win >= bet * BIG_WIN_MULT;
    if (isBigWin) sfx.bigWin();
    else sfx.win();

    // Phase 1: all winning lines + all winning cells at once, losers dimmed.
    this.overlay.showLines(lineWins.map((lw) => lw.lineIndex));
    this.applyCellStates(winCells);
    await this.wait(ALL_LINES_MS);
    if (gen !== this.gen) return;

    // Phase 2: win amount — banner for big wins, floating rollup otherwise
    // (rollup runs alongside the line cycle).
    if (isBigWin) {
      await this.banner.play(result.win);
      if (gen !== this.gen) return;
    } else {
      this.startFloatingRollup(result.win);
    }

    // Phase 3: cycle the individual wins. Skip when the flash already told the
    // whole story (a single line, nothing else).
    const items = this.cycleItems(result, lineWins);
    if (items.length === 1 && !result.expanded && !(result.scatterWin ?? 0)) {
      await this.wait(SINGLE_LINE_HOLD_MS);
    } else {
      const perItemMs = Math.min(750, Math.max(450, 3000 / items.length));
      for (let i = 0; i < items.length; i++) {
        if (gen !== this.gen) return;
        const item = items[i];
        this.overlay.clear();
        this.applyCellStates(item.cells);
        sfx.lineTick(i);
        this.callout.text = item.label;
        this.callout.style.fill = item.color;
        this.callout.visible = true;
        if (item.lineIndex !== null) void this.overlay.animateLine(item.lineIndex);
        await this.wait(perItemMs);
      }
    }
    if (gen !== this.gen) return;

    if (this.floatingRollup) {
      this.floatingRollup.finish();
      this.floatingRollup = null;
    }

    // Cleanup.
    this.overlay.clear();
    this.callout.visible = false;
    for (const reel of reels) reel.clearWinning();
  }

  /** Book flash + reel-fill animation for the expanding special. */
  async playExpand(result: SpinResponse): Promise<void> {
    const expanded = result.expanded;
    if (!expanded?.preReels) return; // old server: grid already painted, skip
    const gen = ++this.gen;
    const { cell, rows, reels, textures } = this.opts;

    sfx.expandWhoosh();

    // 1) Flash the cells where the special originally landed.
    const flashes: Graphics[] = [];
    expanded.reels.forEach((col, i) => {
      expanded.preReels![i].forEach((sym, row) => {
        if (sym !== expanded.symbol) return;
        const f = new Graphics();
        const pad = cell * 0.04;
        f.roundRect(col * cell + pad, row * cell + pad, cell - pad * 2, cell - pad * 2, cell * 0.14)
          .fill({ color: 0xffffff });
        f.alpha = 0;
        this.fx.addChild(f);
        flashes.push(f);
      });
    });
    await this.animateFrame(350, (t) => {
      // Two quick pulses: |sin| over two periods.
      const a = Math.abs(Math.sin(t * Math.PI * 2)) * 0.9;
      for (const f of flashes) f.alpha = a;
    });
    for (const f of flashes) f.destroy();
    if (gen !== this.gen) return;

    // 2) Fill each affected column with the special symbol.
    const tex = textures[expanded.symbol];
    const sprites: Sprite[] = [];
    const sweeps: Graphics[] = [];
    if (tex && tex.width) {
      for (const col of expanded.reels) {
        const s = new Sprite(tex);
        s.anchor.set(0.5);
        s.position.set((col + 0.5) * cell, (rows * cell) / 2);
        const base = Math.min((cell * 0.86) / tex.width, (cell * 0.86) / tex.height);
        s.scale.set(base);
        this.fx.addChild(s);
        sprites.push(s);

        const sw = new Graphics();
        sw.rect(col * cell + 2, 0, cell - 4, cell * 0.5).fill({ color: 0xffe9a8, alpha: 0.35 });
        this.fx.addChild(sw);
        sweeps.push(sw);
      }
      const targetY = (rows * cell * 0.95);
      await this.animateFrame(450, (t) => {
        const e = 1 - Math.pow(1 - t, 3);
        for (const s of sprites) {
          const base = Math.min((cell * 0.86) / tex.width, (cell * 0.86) / tex.height);
          s.scale.x = base * (1 + 0.1 * e);
          s.scale.y = base + (targetY / tex.height - base) * e;
        }
        for (const sw of sweeps) sw.y = -cell * 0.5 + rows * cell * 1.2 * e;
      });
    }
    if (gen === this.gen) {
      // Commit the painted grid to the actual cells, then drop the temp art.
      expanded.reels.forEach((col) => {
        for (let r = 0; r < rows; r++) reels[col].getCell(r).set(expanded.symbol);
      });
    }
    for (const s of sprites) s.destroy();
    for (const sw of sweeps) sw.destroy();
    if (gen !== this.gen) return;
    await this.wait(250);
  }

  destroy(): void {
    this.cancel();
    this.overlay.destroy();
    this.banner.destroy();
  }

  private cycleItems(result: SpinResponse, lineWins: LineWin[]) {
    const { rows } = this.opts;
    const fmt = this.opts.fmt;
    const items: Array<{ label: string; color: number; cells: Set<string>; lineIndex: number | null }> = [];

    if (result.expanded) {
      const cells = new Set<string>();
      for (const col of result.expanded.reels) for (let r = 0; r < rows; r++) cells.add(`${col},${r}`);
      const amount = result.expandWin ?? 0;
      items.push({
        label: `${result.expanded.symbol.toUpperCase()} × ${result.expanded.reels.length} REELS${amount ? ` · +${fmt(amount)}` : ''}`,
        color: 0xffd45e,
        cells,
        lineIndex: null,
      });
    }
    if ((result.scatterWin ?? 0) > 0) {
      const cells = new Set<string>();
      result.reels.forEach((column, col) =>
        column.forEach((s, row) => s === 'book' && cells.add(`${col},${row}`)));
      items.push({ label: `SCATTER · +${fmt(result.scatterWin!)}`, color: 0xffe9a8, cells, lineIndex: null });
    }
    for (const lw of lineWins) {
      const line = this.paylines[lw.lineIndex];
      if (!line) continue;
      const cells = new Set<string>();
      for (let col = 0; col < lw.count; col++) cells.add(`${col},${line[col]}`);
      items.push({
        label: `LINE ${lw.lineIndex + 1}${lw.amount ? ` · +${fmt(lw.amount)}` : ''}`,
        color: lineColor(lw.lineIndex),
        cells,
        lineIndex: lw.lineIndex,
      });
    }
    return items;
  }

  private applyCellStates(winCells: Set<string>): void {
    const { reelsCount, rows, reels } = this.opts;
    for (let col = 0; col < reelsCount; col++) {
      for (let row = 0; row < rows; row++) {
        reels[col].setWinState(row, winCells.has(`${col},${row}`) ? 'win' : 'dim');
      }
    }
  }

  private startFloatingRollup(win: number): void {
    const { cell, reelsCount, rows } = this.opts;
    this.floating.text = this.opts.fmt(0);
    this.floating.alpha = 1;
    this.floating.position.set((reelsCount * cell) / 2, (rows * cell) / 2);
    this.floating.visible = true;
    const ms = Math.min(1100, 400 + win * 4);
    sfx.rollup(ms);
    const handle = rollup(0, win, ms, (v) => (this.floating.text = `+${this.opts.fmt(v)}`));
    this.floatingRollup = handle;
    void handle.promise.then(() => {
      if (this.floatingRollup === handle) this.floatingRollup = null;
      // Rise + fade out.
      const startY = this.floating.position.y;
      void this.animateFrame(300, (t) => {
        this.floating.position.y = startY - 24 * t;
        this.floating.alpha = 1 - t;
      }).then(() => (this.floating.visible = false));
    });
  }

  private wait(ms: number): Promise<void> {
    return new Promise((res) => setTimeout(res, ms));
  }

  private animateFrame(ms: number, onFrame: (t: number) => void): Promise<void> {
    return new Promise((res) => {
      let elapsed = 0;
      const cb = (t: Ticker) => {
        elapsed += t.deltaMS;
        const p = Math.min(1, elapsed / ms);
        onFrame(p);
        if (p >= 1) {
          this.opts.ticker.remove(cb);
          res();
        }
      };
      this.opts.ticker.add(cb);
    });
  }
}
